import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';

import { Icon } from '@column/column-ui-kit';
import { Badge, DataTable, type DataTableColumnDef } from '@column/ui';

import { ROUTE } from '~/app/routes';
import { Loading, Section } from '~/components';
import { Empty as TableEmpty, getTimestampColumn, tableDefaults, useTimeFormat } from '~/components/Table';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { CardToken, CardTokenRepository } from '~/repositories/CardTokenRepository';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { formatString } from '~/util';

import { CardProps } from './Card';

const getColumns = (
  timeFormat: TableTimeFormat,
  onTimeFormatChange: (value: TableTimeFormat) => void
): DataTableColumnDef<CardToken>[] => [
  {
    header: 'Token ID',
    accessor: 'id',
    width: 'minmax(min-content, 1fr)',
    cell: ({ value }) => (
      <DataTable.TextCell displayVariant="monospaced" weight="medium" value={(value as string) || '-'} />
    ),
  },
  {
    header: 'Wallet Type',
    accessor: 'walletType',
    width: 'minmax(min-content, 1fr)',
    cell: ({ value }) => <Badge variant="secondary">{value ? formatString(value as string) : '-'}</Badge>,
  },
  {
    header: 'Token Type',
    accessor: 'tokenType',
    width: 'minmax(min-content, 1fr)',
    cell: ({ value }) => <Badge variant="secondary">{value ? formatString(value as string) : '-'}</Badge>,
  },
  {
    header: 'Device Name',
    accessor: 'deviceName',
    width: 'minmax(min-content, 1fr)',
    cell: ({ value }) => <DataTable.TextCell value={(value as string) || '-'} />,
  },
  {
    header: 'Status',
    accessor: 'status',
    width: 'minmax(min-content, 1fr)',
    cell: ({ value }) => <Badge variant="secondary">{value ? formatString(value as string) : '-'}</Badge>,
  },
  getTimestampColumn<CardToken>({
    accessor: 'createdAt',
    timeFormat,
    onTimeFormatChange,
    width: 'minmax(min-content, 1fr)',
    fallback: '-',
  }),
];

export const PageCardTokens: React.FC = () => {
  const navigate = useNavigate();
  const { card } = useOutletContext<CardProps>();
  const { currentPlatform, isSandbox } = useSessionStore();
  const {
    cursor,
    hasPrevious,
    onNext,
    onPrevious,
    pageIndex,
    pageSize,
    reset: resetPagination,
    setPageSize,
  } = usePaginationState();
  const { timeFormat, onTimeFormatChange } = useTimeFormat('card-tokens-by-card');

  const isInitialRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }
    resetPagination();
  }, [card?.id, currentPlatform?.id, isSandbox, resetPagination]);

  const {
    data: tokensData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['card-tokens', 'by-card', card?.id, currentPlatform?.id, isSandbox, pageSize, cursor],
    queryFn: () => CardTokenRepository.listCardTokens({ cardId: card.id, limit: pageSize, startingAfter: cursor }),
    enabled: !!card?.id,
    placeholderData: keepPreviousData,
  });
  const cardTokens = tokensData?.cardTokens ?? [];
  const hasMore = Boolean(tokensData?.hasMore);

  return (
    <Inner p={0}>
      <Section headline="Tokens">
        <Loading isLoading={isLoading}>
          <Inner px="var(--page-margin)" py={0}>
            <DataTable<CardToken>
              {...tableDefaults}
              columns={getColumns(timeFormat, onTimeFormatChange)}
              data={cardTokens}
              rowKey={(row) => row.id}
              isLoading={isFetching}
              emptyContent={
                <TableEmpty
                  icon={<Icon.AnimationWallet />}
                  title="No tokens yet"
                  description="Tokens will appear here for this card."
                />
              }
              pagination={{
                hasNext: hasMore,
                hasPrevious,
                pageIndex,
                pageSize,
                onNext: () => {
                  if (cardTokens.length > 0) {
                    onNext({ cursor: cardTokens[cardTokens.length - 1].id });
                  }
                },
                onPrevious,
                onPageSizeChange: setPageSize,
              }}
              onRowClick={(row) => {
                navigate(`${ROUTE.CARD_TOKENS}/edit/${row.id}`);
              }}
            />
          </Inner>
        </Loading>
      </Section>
    </Inner>
  );
};
